import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { can, MIN_REASON } from '@urbanrats/ops-core';
import { CountryPicker, countryName } from '../components/CountryPicker.tsx';
import { Link } from '../components/Link.tsx';
import { RealMoney } from './RealMoney.tsx';
import { PageHead, Section } from '../components/common.tsx';
import { fmtTime, useCtx } from '../lib.ts';

interface Rg {
  enabled?: boolean;
  realityCheckMin?: number | null;
  sessionLimitMin?: number | null;
}
interface BrandView {
  brandId: string;
  name: string;
  walletCurrency: string;
  allowedOrigins: string[];
  allowedCountries: string[];
  displayCurrencies: string[];
  status: string;
  defaultLobbyUrl: string | null;
  defaultDepositUrl: string | null;
  rg: Rg | null;
  createdAt: string;
  games: { gameId: string; enabled: boolean; countries: string[] }[];
  siteId: string | null;
  createdVia: 'ADMIN' | 'AUTO';
  moneyMode: 'DEMO' | 'REAL';
}
interface SiteTemplate {
  autoSites: boolean;
  currencies: string[];
  countries: string[];
  displayCurrencies: string[];
  games: string[];
  originPolicy: 'LIST' | 'DECLARED';
  allowedOrigins: string[];
  maxSites: number;
}
interface OperatorView {
  operatorId: string;
  aggregatorId: string | null;
  kind: 'OPERATOR' | 'AGGREGATOR';
  siteTemplate: SiteTemplate | null;
  name: string;
  status: string;
  contactEmail: string | null;
  notes: string | null;
  rg: Rg;
  createdAt: string;
  players: number;
  bets24h: number;
  integration: { adapter: string; status: string; updatedAt: string } | null;
  keys: Record<string, number>;
  hosts: {
    host: string;
    purpose: string;
    addedBy: string | null;
    createdAt: string;
  }[];
  wallet: {
    mode: string;
    baseUrl: string | null;
    keyId: string | null;
    secretsSet: boolean;
    wireScales: Record<string, number>;
    idempotent: boolean;
    statusInquiry: boolean;
    deadlines: Record<string, number>;
  };
  brands: BrandView[];
}
interface View {
  games: { game_id: string; retired: boolean }[];
  currencies: string[];
  operators: OperatorView[];
}
type Post = <T>(path: string, body: Record<string, unknown>) => Promise<{ ok: true; value: T } | { ok: false; error: string }>;

const csv = (s: string) =>
  s
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * Operator onboarding (API mode): operators, sites (brands), game entitlements, the seamless-wallet
 * connection with secrets shown once, callback hosts and responsible-gaming switches. Every change needs a
 * reason and is in the audit log; only platform admins (`onboarding.manage`) can change anything.
 */
export function OperatorsAdmin() {
  const { backend, user } = useCtx();
  const server = backend.server!;
  const [view, setView] = useState<View | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // A link (e.g. a notification) may open one operator and tab: /operators?operator=…&tab=money
  const q = new URLSearchParams(location.search);
  const [open, setOpen] = useState<string | null>(q.get('operator'));
  const [creating, setCreating] = useState(false);
  const linkTab = (q.get('tab') ?? undefined) as Tab | undefined;
  const load = useCallback(async () => {
    const r = await server.get<View>('/v1/admin/operators');
    if (r.ok) {
      setView(r.value);
      setErr(null);
    } else setErr(r.error);
  }, [server]);
  useEffect(() => {
    const t = setTimeout(() => void load(), 0);
    return () => clearTimeout(t);
  }, [load]);
  const manage = can(user, 'onboarding.manage');
  const post: Post = (p, b) => server.post(p, b);
  if (!view) return <p className="empty">{err ?? 'Loading operators…'}</p>;
  const current = view.operators.find((o) => o.operatorId === open) ?? null;
  return (
    <div className="opadmin" data-testid="operators-admin">
      <PageHead title="Operators and sites">
        An operator is the company we contract with; a site (brand) is one casino they run: its wallet currency, countries, page addresses and games. Every change needs a reason and is written to the audit log.
      </PageHead>
      <div className="op-toolbar">
        <span className="muted">
          {view.operators.length} operator
          {view.operators.length === 1 ? '' : 's'} · {view.operators.reduce((s, o) => s + o.brands.length, 0)} sites
        </span>
        {manage && (
          <button type="button" className="btn primary" onClick={() => setCreating((v) => !v)} data-testid="op-new">
            {creating ? 'Close' : '+ New operator'}
          </button>
        )}
      </div>
      {creating && (
        <NewOperator
          post={post}
          done={async (id) => {
            setCreating(false);
            await load();
            setOpen(id);
          }}
        />
      )}
      <div className="op-list">
        {view.operators.map((o) => (
          <button type="button" key={o.operatorId} className={`op-card ${open === o.operatorId ? 'on' : ''}`} onClick={() => setOpen(open === o.operatorId ? null : o.operatorId)} data-testid={`op-card-${o.operatorId}`}>
            <span className="op-card-top">
              <strong>{o.name}</strong>
              <span className={`tag st-${o.status}`}>{o.status}</span>
            </span>
            <span className="mono muted">
              {o.operatorId}
              {o.aggregatorId ? ` · via ${o.aggregatorId}` : ''}
            </span>
            <span className="op-card-stats">
              <span>
                <b>{o.brands.length}</b> sites
              </span>
              <span>
                <b>{o.players}</b> players
              </span>
              <span>
                <b>{o.bets24h}</b> bets 24 h
              </span>
            </span>
            <span className="op-card-foot">
              <span className={`tag ${o.wallet.mode === 'DIRECT' ? 'st-ACCEPTED' : ''}`}>{o.wallet.mode === 'DIRECT' ? 'Seamless wallet' : 'Demo wallet'}</span>
              {o.kind === 'AGGREGATOR' && <span className="tag st-ACCEPTED">Aggregator · {new Set(o.brands.map((b) => b.siteId ?? b.brandId)).size} sites</span>}
              <span className="tag">{o.integration?.status ?? 'NOT_STARTED'}</span>
            </span>
          </button>
        ))}
      </div>
      {current && <OperatorDetail key={current.operatorId} o={current} view={view} post={post} manage={manage} reload={load} initialTab={current.operatorId === q.get('operator') ? linkTab : undefined} />}
    </div>
  );
}

function useReason() {
  const [reason, setReason] = useState('');
  return { reason, setReason, ok: reason.trim().length >= MIN_REASON };
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="op-field">
      <span>{label}</span>
      {children}
      {hint && <small className="muted">{hint}</small>}
    </label>
  );
}

function Status({ msg }: { msg: { ok: boolean; text: string } | null }) {
  if (!msg) return null;
  return (
    <p role="status" className={`op-msg ${msg.ok ? 'ok' : 'bad'}`}>
      {msg.text}
    </p>
  );
}

function NewOperator({ post, done }: { post: Post; done: (id: string) => Promise<void> }) {
  const [f, setF] = useState({
    operatorId: '',
    name: '',
    contactEmail: '',
    aggregatorId: '',
    notes: '',
  });
  const { reason, setReason, ok } = useReason();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  return (
    <Section title="New operator">
      <form
        className="op-form"
        onSubmit={async (e) => {
          e.preventDefault();
          const r = await post<{ operatorId: string }>('/v1/admin/operators', {
            ...f,
            reason,
          });
          if (!r.ok) return setMsg({ ok: false, text: r.error });
          await done(r.value.operatorId);
        }}
      >
        <div className="op-grid">
          <Field label="Operator id" hint="lowercase letters, digits, dashes — permanent">
            <input value={f.operatorId} onChange={(e) => setF({ ...f, operatorId: e.target.value.toLowerCase() })} placeholder="acme-gaming" data-testid="op-new-id" />
          </Field>
          <Field label="Company name">
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Acme Gaming Ltd" data-testid="op-new-name" />
          </Field>
          <Field label="Technical contact e-mail">
            <input type="email" value={f.contactEmail} onChange={(e) => setF({ ...f, contactEmail: e.target.value })} placeholder="tech@acme.example" />
          </Field>
          <Field label="Aggregator (optional)" hint="empty = direct integration">
            <input value={f.aggregatorId} onChange={(e) => setF({ ...f, aggregatorId: e.target.value })} />
          </Field>
        </div>
        <Field label="Notes">
          <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} />
        </Field>
        <Field label={`Reason (min ${MIN_REASON} chars)`}>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Signed integration agreement 2026-10" data-testid="op-new-reason" />
        </Field>
        <div className="op-actions">
          <button type="submit" className="btn primary" disabled={!ok || !f.operatorId || !f.name} data-testid="op-new-save">
            Create operator
          </button>
          <Status msg={msg} />
        </div>
      </form>
    </Section>
  );
}

type Tab = 'general' | 'sites' | 'money' | 'aggregator' | 'wallet' | 'hosts' | 'keys';

function OperatorDetail({ o, view, post, manage, reload, initialTab }: { o: OperatorView; view: View; post: Post; manage: boolean; reload: () => Promise<void>; initialTab?: Tab }) {
  const [tab, setTab] = useState<Tab>(initialTab ?? 'sites');
  const real = o.brands.filter((b) => b.moneyMode === 'REAL').length;
  const tabs: [Tab, string][] = [
    ['general', 'General'],
    ['sites', `Sites (${o.brands.length})`],
    ['money', real ? `Real money · ${real} on` : 'Real money'],
    ['aggregator', o.kind === 'AGGREGATOR' ? 'Aggregator mode · on' : 'Aggregator mode'],
    ['wallet', 'Wallet'],
    ['hosts', `Callback hosts (${o.hosts.length})`],
    ['keys', 'API keys & integration'],
  ];
  return (
    <section className="op-detail" data-testid={`op-detail-${o.operatorId}`}>
      <header className="op-detail-head">
        <div>
          <h2>{o.name}</h2>
          <span className="mono muted">
            {o.operatorId} · created {fmtTime(o.createdAt)}
            {o.contactEmail ? ` · ${o.contactEmail}` : ''}
          </span>
        </div>
        <span className={`tag st-${o.status}`}>{o.status}</span>
      </header>
      <nav className="op-tabs" role="tablist">
        {tabs.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)} data-testid={`op-tab-${k}`}>
            {l}
          </button>
        ))}
      </nav>
      {tab === 'general' && <General o={o} post={post} manage={manage} reload={reload} />}
      {tab === 'sites' && <Sites o={o} view={view} post={post} manage={manage} reload={reload} />}
      {tab === 'money' && <RealMoney operatorId={o.operatorId} manage={manage} />}
      {tab === 'aggregator' && <Aggregator o={o} view={view} post={post} manage={manage} reload={reload} />}
      {tab === 'wallet' && <Wallet o={o} view={view} post={post} manage={manage} reload={reload} />}
      {tab === 'hosts' && <Hosts o={o} post={post} manage={manage} reload={reload} />}
      {tab === 'keys' && (
        <div className="op-pane">
          <dl className="kv">
            <dt>Adapter</dt>
            <dd>{o.integration?.adapter ?? 'DIRECT_V1'}</dd>
            <dt>Integration status</dt>
            <dd>
              <span className="tag">{o.integration?.status ?? 'NOT_STARTED'}</span> <span className="muted">INTEGRATION_READY = our conformance suite passed · PARTNER_CERTIFIED = signed partner acceptance</span>
            </dd>
            <dt>Active API keys</dt>
            <dd>
              sandbox {o.keys.SANDBOX ?? 0} · production {o.keys.PRODUCTION ?? 0}
            </dd>
          </dl>
          <p className="note">
            Sandbox keys are issued in the developer portal (private key shown once, never stored). Production keys: the operator generates the pair and sends only the public key; a second platform admin approves the key rotation request.
          </p>
          <Link to={`/developer?operator=${encodeURIComponent(o.operatorId)}`} className="btn">
            Open developer portal for {o.operatorId} →
          </Link>
        </div>
      )}
    </section>
  );
}

function RgEditor({ value, onChange, inherit }: { value: Rg | null; onChange: (v: Rg | null) => void; inherit?: boolean }) {
  const v = value ?? {};
  return (
    <div className="op-rg">
      {inherit && (
        <label className="op-check">
          <input type="checkbox" checked={value === null} onChange={(e) => onChange(e.target.checked ? null : { enabled: false })} /> Use the operator's setting
        </label>
      )}
      {(!inherit || value !== null) && (
        <>
          <label className="op-check">
            <input type="checkbox" checked={!!v.enabled} onChange={(e) => onChange({ ...v, enabled: e.target.checked })} /> Responsible-gaming tools on
          </label>
          <div className="op-grid">
            <Field label="Reality check every (min)" hint="5–1440, empty = off">
              <input
                inputMode="numeric"
                value={v.realityCheckMin ?? ''}
                disabled={!v.enabled}
                onChange={(e) =>
                  onChange({
                    ...v,
                    realityCheckMin: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
            </Field>
            <Field label="Session limit (min)" hint="5–1440, empty = none">
              <input
                inputMode="numeric"
                value={v.sessionLimitMin ?? ''}
                disabled={!v.enabled}
                onChange={(e) =>
                  onChange({
                    ...v,
                    sessionLimitMin: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
            </Field>
          </div>
        </>
      )}
    </div>
  );
}

function General({ o, post, manage, reload }: { o: OperatorView; post: Post; manage: boolean; reload: () => Promise<void> }) {
  const [f, setF] = useState({
    name: o.name,
    contactEmail: o.contactEmail ?? '',
    notes: o.notes ?? '',
    aggregatorId: o.aggregatorId ?? '',
  });
  const [rg, setRg] = useState<Rg | null>(o.rg ?? {});
  const { reason, setReason, ok } = useReason();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const send = async (body: Record<string, unknown>, done: string) => {
    const r = await post(`/v1/admin/operators/${encodeURIComponent(o.operatorId)}`, { ...body, reason });
    setMsg(r.ok ? { ok: true, text: done } : { ok: false, text: r.error });
    if (r.ok) {
      setReason('');
      await reload();
    }
  };
  return (
    <form
      className="op-pane op-form"
      onSubmit={(e) => {
        e.preventDefault();
        void send({ ...f, rg }, 'Saved.');
      }}
    >
      <div className="op-grid">
        <Field label="Company name">
          <input value={f.name} disabled={!manage} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label="Technical contact e-mail">
          <input value={f.contactEmail} disabled={!manage} onChange={(e) => setF({ ...f, contactEmail: e.target.value })} />
        </Field>
        <Field label="Aggregator">
          <input value={f.aggregatorId} disabled={!manage} onChange={(e) => setF({ ...f, aggregatorId: e.target.value })} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea value={f.notes} disabled={!manage} rows={2} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </Field>
      <h4>Responsible gaming (default for all sites)</h4>
      <RgEditor value={rg} onChange={setRg} />
      {manage && (
        <>
          <Field label={`Reason (min ${MIN_REASON} chars)`}>
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <div className="op-actions">
            <button type="submit" className="btn primary" disabled={!ok}>
              Save
            </button>
            {o.status === 'ACTIVE' ? (
              <button type="button" className="btn danger" disabled={!ok} onClick={() => void send({ status: 'SUSPENDED' }, 'Operator suspended: no new launches on any of its sites.')} data-testid="op-suspend">
                Suspend operator
              </button>
            ) : (
              <button type="button" className="btn" disabled={!ok} onClick={() => void send({ status: 'ACTIVE' }, 'Operator active again.')}>
                Reactivate operator
              </button>
            )}
            <Status msg={msg} />
          </div>
        </>
      )}
    </form>
  );
}

const emptySite = {
  brandId: '',
  name: '',
  walletCurrency: 'EUR',
  allowedCountries: '',
  displayCurrencies: '',
  allowedOrigins: '',
  defaultLobbyUrl: '',
  defaultDepositUrl: '',
};

function Sites({ o, view, post, manage, reload }: { o: OperatorView; view: View; post: Post; manage: boolean; reload: () => Promise<void> }) {
  const [adding, setAdding] = useState(o.brands.length === 0 && o.kind !== 'AGGREGATOR');
  const [edit, setEdit] = useState<string | null>(null);
  const site = useReason();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const live = view.games.filter((g) => !g.retired);
  const siteAction = async (siteId: string, status: 'ACTIVE' | 'SUSPENDED') => {
    const r = await post<{ brands: number }>(`/v1/admin/operators/${encodeURIComponent(o.operatorId)}/sites/${encodeURIComponent(siteId)}`, { status, reason: site.reason });
    setMsg(
      r.ok
        ? {
            ok: true,
            text: `Site ${siteId}: ${status === 'SUSPENDED' ? 'suspended' : 'active again'} (${r.value.brands} currency brand${r.value.brands === 1 ? '' : 's'}).`,
          }
        : { ok: false, text: r.error },
    );
    if (r.ok) {
      site.setReason('');
      await reload();
    }
  };
  return (
    <div className="op-pane">
      {o.kind === 'AGGREGATOR' && (
        <p className="note">
          Aggregator: sites appear here automatically on their first launch (one row per site and wallet currency, id <code>site.currency</code>). Suspending a site stops new launches in all its currencies.
        </p>
      )}
      {o.kind === 'AGGREGATOR' && manage && o.brands.some((b) => b.siteId) && (
        <Field label={`Reason for site actions (min ${MIN_REASON} chars)`}>
          <input value={site.reason} onChange={(e) => site.setReason(e.target.value)} placeholder="Customer site closed by the aggregator" />
        </Field>
      )}
      <Status msg={msg} />
      {o.brands.length === 0 && !adding && <p className="empty">No site yet.</p>}
      {o.brands.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Site</th>
                <th>Wallet currency</th>
                <th>Money</th>
                <th>Countries</th>
                <th>Page addresses</th>
                <th>Games</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {o.brands.map((b) => (
                <tr key={b.brandId} data-testid={`site-${b.brandId}`}>
                  <td>
                    <strong>{b.name}</strong>
                    <div className="mono muted">
                      {b.brandId}
                      {b.siteId && b.siteId !== b.brandId ? ` · site ${b.siteId}` : ''}
                    </div>
                    {b.createdVia === 'AUTO' && <span className="tag">auto</span>}
                  </td>
                  <td className="mono">{b.walletCurrency}</td>
                  <td>
                    <span className={`tag ${b.moneyMode === 'REAL' ? 'money-real' : ''}`} data-testid={`site-money-${b.brandId}`}>
                      {b.moneyMode === 'REAL' ? 'REAL money' : 'Demo'}
                    </span>
                  </td>
                  <td title={b.allowedCountries.map(countryName).join(', ')}>{b.allowedCountries.length > 12 ? `${b.allowedCountries.length} countries` : b.allowedCountries.join(' ')}</td>
                  <td className="mono small">{b.allowedOrigins.join(' ') || '—'}</td>
                  <td>
                    {b.games.map((g) => (
                      <span key={g.gameId} className={`tag ${g.enabled ? 'st-SETTLED' : 'st-VOIDED'}`}>
                        {g.gameId}
                        {g.countries.length ? ` (${g.countries.join(',')})` : ''}
                      </span>
                    ))}
                  </td>
                  <td>
                    <span className={`tag st-${b.status}`}>{b.status}</span>
                  </td>
                  <td>
                    {manage && (
                      <button type="button" className="btn ghost" onClick={() => setEdit(edit === b.brandId ? null : b.brandId)}>
                        {edit === b.brandId ? 'Close' : 'Edit'}
                      </button>
                    )}
                    {manage && b.siteId && (
                      <button type="button" className="btn ghost" disabled={!site.ok} title={site.ok ? undefined : 'Enter a reason above'} onClick={() => void siteAction(b.siteId!, b.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE')}>
                        {b.status === 'ACTIVE' ? 'Suspend site' : 'Activate site'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && <SiteForm key={edit} o={o} view={view} brand={o.brands.find((b) => b.brandId === edit)!} post={post} done={async () => (setEdit(null), await reload())} />}
      {manage && !adding && (
        <button type="button" className="btn" onClick={() => setAdding(true)} data-testid="site-add">
          + Add site
        </button>
      )}
      {manage && adding && <SiteForm o={o} view={view} brand={null} post={post} done={async () => (setAdding(false), await reload())} />}
      {live.length === 0 && <p className="note">No game is offered right now.</p>}
    </div>
  );
}

function SiteForm({ o, view, brand, post, done }: { o: OperatorView; view: View; brand: BrandView | null; post: Post; done: () => Promise<void> }) {
  const [f, setF] = useState(
    brand
      ? {
          brandId: brand.brandId,
          name: brand.name,
          walletCurrency: brand.walletCurrency,
          allowedCountries: brand.allowedCountries.join(' '),
          displayCurrencies: brand.displayCurrencies.join(' '),
          allowedOrigins: brand.allowedOrigins.join(' '),
          defaultLobbyUrl: brand.defaultLobbyUrl ?? '',
          defaultDepositUrl: brand.defaultDepositUrl ?? '',
        }
      : emptySite,
  );
  const live = view.games.filter((g) => !g.retired).map((g) => g.game_id);
  const [games, setGames] = useState<Record<string, { enabled: boolean; countries: string }>>(() =>
    Object.fromEntries(
      live.map((g) => {
        const e = brand?.games.find((x) => x.gameId === g);
        return [
          g,
          {
            enabled: brand ? !!e?.enabled : true,
            countries: e?.countries.join(' ') ?? '',
          },
        ];
      }),
    ),
  );
  const [rg, setRg] = useState<Rg | null>(brand ? brand.rg : null);
  const { reason, setReason, ok } = useReason();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const base = `/v1/admin/operators/${encodeURIComponent(o.operatorId)}/brands`;
  const save = async (extra: Record<string, unknown> = {}) => {
    const body = {
      name: f.name,
      walletCurrency: f.walletCurrency,
      allowedCountries: csv(f.allowedCountries.toUpperCase()),
      displayCurrencies: csv(f.displayCurrencies.toUpperCase()),
      allowedOrigins: csv(f.allowedOrigins),
      defaultLobbyUrl: f.defaultLobbyUrl,
      defaultDepositUrl: f.defaultDepositUrl,
      rg,
      reason,
      ...extra,
    };
    const r = brand
      ? await post(`${base}/${encodeURIComponent(brand.brandId)}`, body)
      : await post(base, {
          ...body,
          brandId: f.brandId,
          games: live.filter((g) => games[g]?.enabled),
        });
    if (!r.ok) return setMsg({ ok: false, text: r.error });
    const id = brand?.brandId ?? f.brandId;
    // Entitlements: one call per game whose switch or countries changed.
    for (const g of live) {
      const want = games[g]!;
      const had = brand?.games.find((x) => x.gameId === g);
      const changed = !brand ? want.countries.trim() !== '' : !!had?.enabled !== want.enabled || (had?.countries.join(' ') ?? '') !== want.countries.trim();
      if (!changed) continue;
      const e = await post(`${base}/${encodeURIComponent(id)}/games`, {
        gameId: g,
        enabled: want.enabled,
        countries: csv(want.countries.toUpperCase()),
        reason,
      });
      if (!e.ok) return setMsg({ ok: false, text: `${g}: ${e.error}` });
    }
    await done();
  };
  return (
    <form
      className="op-form op-site-form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      data-testid={brand ? `site-form-${brand.brandId}` : 'site-form-new'}
    >
      <h4>{brand ? `Edit ${brand.name}` : 'New site'}</h4>
      <div className="op-grid">
        <Field label="Site id" hint={brand ? 'permanent' : 'lowercase, digits, dashes — permanent'}>
          <input value={f.brandId} disabled={!!brand} onChange={(e) => setF({ ...f, brandId: e.target.value.toLowerCase() })} placeholder="acme-tr" data-testid="site-id" />
        </Field>
        <Field label="Site name">
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Acme Casino TR" data-testid="site-name" />
        </Field>
        <Field label="Wallet currency" hint="the money of this site; locked once players have played">
          <select value={f.walletCurrency} onChange={(e) => setF({ ...f, walletCurrency: e.target.value })} data-testid="site-currency">
            {view.currencies.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Allowed countries" hint="ISO codes, e.g. TR DE AT, or add a whole region (the operator declares the player's country)">
          <CountryPicker value={f.allowedCountries} onChange={(v) => setF({ ...f, allowedCountries: v })} testId="site-countries" />
        </Field>
        <Field label="Display currencies" hint="optional, shown as approximate amounts">
          <input value={f.displayCurrencies} onChange={(e) => setF({ ...f, displayCurrencies: e.target.value })} placeholder="EUR USD" />
        </Field>
        <Field label="Page addresses (embed origins)" hint="exact origins that may embed the game, e.g. https://casino.example">
          <input value={f.allowedOrigins} onChange={(e) => setF({ ...f, allowedOrigins: e.target.value })} placeholder="https://casino.example" data-testid="site-origins" />
        </Field>
        <Field label="Lobby URL" hint="where a player returns (default when the launch has none)">
          <input value={f.defaultLobbyUrl} onChange={(e) => setF({ ...f, defaultLobbyUrl: e.target.value })} placeholder="https://casino.example/lobby" />
        </Field>
        <Field label="Deposit URL" hint="cashier page (default)">
          <input value={f.defaultDepositUrl} onChange={(e) => setF({ ...f, defaultDepositUrl: e.target.value })} placeholder="https://casino.example/deposit" />
        </Field>
      </div>
      <h4>Games</h4>
      <div className="op-games">
        {live.map((g) => (
          <div key={g} className="op-game">
            <label className="op-check">
              <input
                type="checkbox"
                checked={games[g]!.enabled}
                onChange={(e) =>
                  setGames({
                    ...games,
                    [g]: { ...games[g]!, enabled: e.target.checked },
                  })
                }
                data-testid={`site-game-${g}`}
              />{' '}
              <span className="mono">{g}</span>
            </label>
            <input
              value={games[g]!.countries}
              placeholder="countries (empty = all of the site)"
              onChange={(e) =>
                setGames({
                  ...games,
                  [g]: { ...games[g]!, countries: e.target.value },
                })
              }
            />
          </div>
        ))}
      </div>
      <h4>Responsible gaming</h4>
      <RgEditor value={rg} onChange={setRg} inherit />
      <Field label={`Reason (min ${MIN_REASON} chars)`}>
        <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="site-reason" />
      </Field>
      <div className="op-actions">
        <button type="submit" className="btn primary" disabled={!ok || !f.name || (!brand && !f.brandId)} data-testid="site-save">
          {brand ? 'Save site' : 'Create site'}
        </button>
        {brand &&
          (brand.status === 'ACTIVE' ? (
            <button type="button" className="btn danger" disabled={!ok} onClick={() => void save({ status: 'SUSPENDED' })}>
              Suspend site
            </button>
          ) : (
            <button type="button" className="btn" disabled={!ok} onClick={() => void save({ status: 'ACTIVE' })}>
              Reactivate site
            </button>
          ))}
        <Status msg={msg} />
      </div>
    </form>
  );
}

function Wallet({ o, view, post, manage, reload }: { o: OperatorView; view: View; post: Post; manage: boolean; reload: () => Promise<void> }) {
  const w = o.wallet;
  const [f, setF] = useState({
    mode: w.mode,
    baseUrl: w.baseUrl ?? '',
    wireScales: Object.entries(w.wireScales)
      .map(([k, v]) => `${k}:${v}`)
      .join(' '),
    idempotent: w.idempotent,
    statusInquiry: w.statusInquiry,
    betTimeoutMs: String(w.deadlines.betTimeoutMs ?? 2500),
    settleTimeoutMs: String(w.deadlines.settleTimeoutMs ?? 5000),
    settleHorizonH: String(Math.round((w.deadlines.settleHorizonMs ?? 86_400_000) / 3_600_000)),
  });
  const { reason, setReason, ok } = useReason();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [secrets, setSecrets] = useState<{
    keyId: string;
    requestSecret: string;
    responseSecret: string;
  } | null>(null);
  const currencies = [...new Set(o.brands.map((b) => b.walletCurrency))];
  void view;
  return (
    <div className="op-pane">
      <p className="note">
        <b>Demo wallet</b>: we keep demo balances (no real money; new players get the demo balance once). <b>Seamless wallet</b>: we call the operator's <code>/balance</code>, <code>/bet</code>, <code>/win</code>, <code>/rollback</code>,{' '}
        <code>/status</code> under the base URL, signed with HMAC both ways (see the integration reference).
      </p>
      <form
        className="op-form"
        onSubmit={async (e) => {
          e.preventDefault();
          const wireScales = Object.fromEntries(
            csv(f.wireScales)
              .map((x) => x.split(':'))
              .filter((p) => p.length === 2)
              .map(([k, v]) => [k!.toUpperCase(), Number(v)]),
          );
          const r = await post(`/v1/admin/operators/${encodeURIComponent(o.operatorId)}/wallet`, {
            mode: f.mode,
            baseUrl: f.baseUrl,
            wireScales,
            idempotent: f.idempotent,
            statusInquiry: f.statusInquiry,
            deadlines: {
              betTimeoutMs: Number(f.betTimeoutMs),
              settleTimeoutMs: Number(f.settleTimeoutMs),
              settleHorizonMs: Number(f.settleHorizonH) * 3_600_000,
            },
            reason,
          });
          setMsg(
            r.ok
              ? {
                  ok: true,
                  text: 'Wallet connection saved (wallet host registered).',
                }
              : { ok: false, text: r.error },
          );
          if (r.ok) {
            setReason('');
            await reload();
          }
        }}
      >
        <div className="op-grid">
          <Field label="Wallet">
            <select value={f.mode} disabled={!manage} onChange={(e) => setF({ ...f, mode: e.target.value })} data-testid="wallet-mode">
              <option value="DEV">Demo wallet (ours)</option>
              <option value="DIRECT">Seamless wallet (operator)</option>
            </select>
          </Field>
          <Field label="Base URL" hint="https, e.g. https://wallet.casino.example/urbanrats">
            <input value={f.baseUrl} disabled={!manage || f.mode === 'DEV'} onChange={(e) => setF({ ...f, baseUrl: e.target.value })} data-testid="wallet-url" />
          </Field>
          <Field label="Wire scales" hint={`decimals per currency, e.g. ${currencies.map((c) => `${c}:5`).join(' ') || 'EUR:5'}`}>
            <input value={f.wireScales} disabled={!manage} onChange={(e) => setF({ ...f, wireScales: e.target.value })} />
          </Field>
          <Field label="Bet timeout (ms)" hint="per attempt; 500–10000">
            <input inputMode="numeric" value={f.betTimeoutMs} disabled={!manage} onChange={(e) => setF({ ...f, betTimeoutMs: e.target.value })} />
          </Field>
          <Field label="Settle timeout (ms)" hint="1000–30000">
            <input inputMode="numeric" value={f.settleTimeoutMs} disabled={!manage} onChange={(e) => setF({ ...f, settleTimeoutMs: e.target.value })} />
          </Field>
          <Field label="Retry wins/refunds for (hours)" hint="then manual reconciliation with an alert">
            <input inputMode="numeric" value={f.settleHorizonH} disabled={!manage} onChange={(e) => setF({ ...f, settleHorizonH: e.target.value })} />
          </Field>
        </div>
        <label className="op-check">
          <input type="checkbox" checked={f.idempotent} disabled={!manage} onChange={(e) => setF({ ...f, idempotent: e.target.checked })} /> Operator endpoints are idempotent (safe to retry with the same transactionId)
        </label>
        <label className="op-check">
          <input type="checkbox" checked={f.statusInquiry} disabled={!manage} onChange={(e) => setF({ ...f, statusInquiry: e.target.checked })} /> Operator offers <code>/status</code>
        </label>
        {manage && (
          <>
            <Field label={`Reason (min ${MIN_REASON} chars)`}>
              <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="wallet-reason" />
            </Field>
            <div className="op-actions">
              <button type="submit" className="btn primary" disabled={!ok} data-testid="wallet-save">
                Save wallet connection
              </button>
              <Status msg={msg} />
            </div>
          </>
        )}
      </form>
      <h4>Signing secrets</h4>
      <dl className="kv">
        <dt>Key id</dt>
        <dd className="mono">{w.keyId ?? '—'}</dd>
        <dt>Secrets</dt>
        <dd>{w.secretsSet ? 'set (never displayed again)' : 'not set — the seamless wallet cannot be called yet'}</dd>
      </dl>
      {manage && !secrets && (
        <button
          type="button"
          className="btn"
          disabled={!ok}
          title={ok ? '' : 'Enter a reason above first'}
          onClick={async () => {
            if (w.secretsSet && !confirm('Generate new secrets? The operator must switch to them; calls signed with the old ones will fail.')) return;
            const r = await post<{
              keyId: string;
              requestSecret: string;
              responseSecret: string;
            }>(`/v1/admin/operators/${encodeURIComponent(o.operatorId)}/wallet/secrets`, { reason });
            if (!r.ok) return setMsg({ ok: false, text: r.error });
            setSecrets(r.value);
            setReason('');
            await reload();
          }}
          data-testid="wallet-secrets"
        >
          {w.secretsSet ? 'Rotate secrets' : 'Generate secrets'}
        </button>
      )}
      {secrets && (
        <div className="op-secret" data-testid="wallet-secrets-shown">
          <p>
            <b>Shown once.</b> Send both to the operator over a secure channel; we cannot show them again.
          </p>
          <dl className="kv">
            <dt>Key id</dt>
            <dd className="mono">{secrets.keyId}</dd>
            <dt>Request secret</dt>
            <dd className="mono">{secrets.requestSecret}</dd>
            <dt>Response secret</dt>
            <dd className="mono">{secrets.responseSecret}</dd>
          </dl>
          <p className="note">Request secret: we sign every callback with it, the operator verifies. Response secret: the operator signs its responses with it, we verify.</p>
          <div className="op-actions">
            <button type="button" className="btn" onClick={() => void navigator.clipboard?.writeText(`keyId=${secrets.keyId}\nrequestSecret=${secrets.requestSecret}\nresponseSecret=${secrets.responseSecret}\n`)}>
              Copy
            </button>
            <button type="button" className="btn ghost" onClick={() => setSecrets(null)}>
              I have stored them
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Hosts({ o, post, manage, reload }: { o: OperatorView; post: Post; manage: boolean; reload: () => Promise<void> }) {
  const [host, setHost] = useState('');
  const [purpose, setPurpose] = useState<'WALLET' | 'WEBHOOK'>('WEBHOOK');
  const { reason, setReason, ok } = useReason();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  return (
    <div className="op-pane">
      <p className="note">We call only these exact host names (https, public addresses, no redirects). The wallet host is registered automatically when the wallet connection is saved.</p>
      {o.hosts.length === 0 ? (
        <p className="empty">No host registered.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Host</th>
                <th>Purpose</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {o.hosts.map((h) => (
                <tr key={`${h.host}/${h.purpose}`}>
                  <td className="mono">{h.host}</td>
                  <td>
                    <span className="tag">{h.purpose}</span>
                  </td>
                  <td className="muted">
                    {fmtTime(h.createdAt)} {h.addedBy ? `· ${h.addedBy}` : ''}
                  </td>
                  <td>
                    {manage && (
                      <button
                        type="button"
                        className="btn ghost"
                        disabled={!ok}
                        title={ok ? '' : 'Enter a reason below first'}
                        onClick={async () => {
                          const r = await post(`/v1/admin/operators/${encodeURIComponent(o.operatorId)}/hosts/remove`, { host: h.host, purpose: h.purpose, reason });
                          setMsg(r.ok ? { ok: true, text: `Removed ${h.host}` } : { ok: false, text: r.error });
                          if (r.ok) await reload();
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {manage && (
        <form
          className="op-form"
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await post('/v1/admin/developer/hosts', {
              operatorId: o.operatorId,
              host,
              purpose,
              reason,
            });
            setMsg(r.ok ? { ok: true, text: `Registered ${host}` } : { ok: false, text: r.error });
            if (r.ok) {
              setHost('');
              await reload();
            }
          }}
        >
          <div className="op-grid">
            <Field label="Host name" hint="DNS name only — no scheme, path, port or IP">
              <input value={host} onChange={(e) => setHost(e.target.value.trim().toLowerCase())} placeholder="hooks.casino.example" />
            </Field>
            <Field label="Purpose">
              <select value={purpose} onChange={(e) => setPurpose(e.target.value as 'WALLET' | 'WEBHOOK')}>
                <option>WEBHOOK</option>
                <option>WALLET</option>
              </select>
            </Field>
          </div>
          <Field label={`Reason (min ${MIN_REASON} chars)`}>
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <div className="op-actions">
            <button type="submit" className="btn primary" disabled={!ok || !host}>
              Register host
            </button>
            <Status msg={msg} />
          </div>
        </form>
      )}
    </div>
  );
}

/** Aggregator mode: one key, one wallet, many sites; new sites are created on their first launch from this template. */
function Aggregator({ o, view, post, manage, reload }: { o: OperatorView; view: View; post: Post; manage: boolean; reload: () => Promise<void> }) {
  const t = o.siteTemplate;
  const live = view.games.filter((g) => !g.retired).map((g) => g.game_id);
  const [on, setOn] = useState(o.kind === 'AGGREGATOR');
  const [f, setF] = useState({
    currencies: (t?.currencies ?? []).join(' '),
    countries: (t?.countries ?? []).join(' '),
    displayCurrencies: (t?.displayCurrencies ?? []).join(' '),
    allowedOrigins: (t?.allowedOrigins ?? []).join(' '),
    originPolicy: t?.originPolicy ?? 'DECLARED',
    maxSites: String(t?.maxSites ?? 1000),
    autoSites: t?.autoSites ?? true,
    games: t?.games ?? ['traffic-pulse'],
  });
  const { reason, setReason, ok } = useReason();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const sites = new Set(o.brands.filter((b) => b.siteId).map((b) => b.siteId)).size;
  return (
    <form
      className="op-pane op-form"
      onSubmit={async (e) => {
        e.preventDefault();
        const body: Record<string, unknown> = {
          kind: on ? 'AGGREGATOR' : 'OPERATOR',
          reason,
        };
        if (on)
          body.template = {
            currencies: csv(f.currencies.toUpperCase()),
            countries: csv(f.countries.toUpperCase()),
            displayCurrencies: csv(f.displayCurrencies.toUpperCase()),
            allowedOrigins: csv(f.allowedOrigins),
            originPolicy: f.originPolicy,
            maxSites: Number(f.maxSites),
            autoSites: f.autoSites,
            games: f.games,
          };
        const r = await post(`/v1/admin/operators/${encodeURIComponent(o.operatorId)}/aggregator`, body);
        setMsg(
          r.ok
            ? {
                ok: true,
                text: on ? 'Aggregator mode saved.' : 'Back to a regular operator (existing sites keep working).',
              }
            : { ok: false, text: r.error },
        );
        if (r.ok) {
          setReason('');
          await reload();
        }
      }}
    >
      <p className="note">
        For an aggregator or a game company that brings many sites: one API key, one wallet connection, one webhook. It sends <strong>its own site id as brandId</strong> and the <strong>currency</strong>; on the first launch we create the site from
        the template below (one site per currency), so reports, limits, suspension and responsible gaming still work per site. Player ids must be unique across all its sites.{' '}
        {o.kind === 'AGGREGATOR' ? `${sites} site${sites === 1 ? '' : 's'} so far.` : ''}
      </p>
      <label className="op-check">
        <input type="checkbox" checked={on} disabled={!manage} onChange={(e) => setOn(e.target.checked)} data-testid="agg-on" /> Aggregator mode
      </label>
      {on && (
        <>
          <div className="op-grid">
            <Field label="Wallet currencies a site may use" hint={`space separated · catalogue: ${view.currencies.slice(0, 12).join(' ')}…`}>
              <input value={f.currencies} disabled={!manage} onChange={(e) => setF({ ...f, currencies: e.target.value })} placeholder="EUR TRY USDT" data-testid="agg-currencies" />
            </Field>
            <Field label="Player countries allowed" hint="ISO codes; the country is still declared per player at launch">
              <input value={f.countries} disabled={!manage} onChange={(e) => setF({ ...f, countries: e.target.value })} placeholder="TR DE AT" data-testid="agg-countries" />
            </Field>
            <Field label="Display currencies (optional)">
              <input value={f.displayCurrencies} disabled={!manage} onChange={(e) => setF({ ...f, displayCurrencies: e.target.value })} />
            </Field>
            <Field label="Maximum sites" hint="new sites beyond this are refused (SITE_LIMIT)">
              <input inputMode="numeric" value={f.maxSites} disabled={!manage} onChange={(e) => setF({ ...f, maxSites: e.target.value })} />
            </Field>
            <Field label="Page addresses (origins)" hint="DECLARED: any https origin the aggregator declares at launch · LIST: only the list">
              <select
                value={f.originPolicy}
                disabled={!manage}
                onChange={(e) =>
                  setF({
                    ...f,
                    originPolicy: e.target.value as 'LIST' | 'DECLARED',
                  })
                }
              >
                <option value="DECLARED">Accept the origin declared at launch (https)</option>
                <option value="LIST">Only the list below</option>
              </select>
            </Field>
            <Field label="Origins every new site gets (optional)">
              <input value={f.allowedOrigins} disabled={!manage} onChange={(e) => setF({ ...f, allowedOrigins: e.target.value })} placeholder="https://games.aggregator.example" />
            </Field>
          </div>
          <div className="op-grid">
            {live.map((g) => (
              <label key={g} className="op-check">
                <input
                  type="checkbox"
                  checked={f.games.includes(g)}
                  disabled={!manage}
                  onChange={(e) =>
                    setF({
                      ...f,
                      games: e.target.checked ? [...f.games, g] : f.games.filter((x) => x !== g),
                    })
                  }
                />{' '}
                {g}
              </label>
            ))}
            <label className="op-check">
              <input type="checkbox" checked={f.autoSites} disabled={!manage} onChange={(e) => setF({ ...f, autoSites: e.target.checked })} /> Create new sites automatically on first launch
            </label>
          </div>
        </>
      )}
      {manage && (
        <>
          <Field label={`Reason (min ${MIN_REASON} chars)`}>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Aggregator agreement: sites self-provisioned" data-testid="agg-reason" />
          </Field>
          <div className="op-actions">
            <button type="submit" className="btn primary" disabled={!ok || (on && (!f.currencies.trim() || !f.countries.trim()))} data-testid="agg-save">
              Save
            </button>
            <Status msg={msg} />
          </div>
        </>
      )}
    </form>
  );
}
